import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, BarChart3, Layers3, Zap } from 'lucide-react';
import { useAuth } from './AuthProvider';

const PRICING_TIERS = [
  {
    id: 'payg',
    name: 'PAYG',
    eyebrow: 'Pay as you go',
    priceLabel: '£0.08',
    priceUnit: '/call',
    trial: '14-day free trial',
    description: 'Start immediately with no monthly commitment.',
    subtext: 'Ideal for evaluation, low-volume integrations, or testing the signal in production.',
    icon: Zap,
    features: [
      'RegretRisk decision engine',
      'Full API access',
      'Usage dashboard',
      'No monthly minimum',
      '14-day free trial',
    ],
    cta: 'Start Free Trial',
    highlighted: false,
    badge: null,
  },
  {
    id: 'growth',
    name: 'Growth',
    eyebrow: 'For live integration',
    priceLabel: '£299',
    priceUnit: '/mo',
    trial: '14-day free trial',
    description: 'Put booking decisions into production.',
    subtext: 'Includes 10,000 API calls per month. Overage at £0.05/call.',
    icon: Layers3,
    features: [
      'RegretRisk decision engine',
      '10,000 calls/month included',
      'Advanced analytics',
      'Webhook callbacks',
      'Priority support',
    ],
    cta: 'Start 14-Day Trial',
    highlighted: true,
    badge: 'Most Popular',
  },
  {
    id: 'scale',
    name: 'Scale',
    eyebrow: 'For scaled teams',
    priceLabel: '£799',
    priceUnit: '/mo',
    trial: '14-day free trial',
    description: 'Optimise booking performance at scale.',
    subtext: 'Includes 50,000 API calls per month. Overage at £0.025/call.',
    icon: BarChart3,
    features: [
      'Everything in Growth',
      '50,000 calls/month included',
      'White-label analytics',
      'Dedicated support',
      '99.9% uptime SLA',
    ],
    cta: 'Start 14-Day Trial',
    highlighted: false,
    badge: 'For Revenue Teams',
  },
];

export default function PricingSection() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const startCheckout = async (tier) => {
    try {
      if (!user?.email) {
        throw new Error('No user email found. Please sign in again.');
      }

      setCheckoutLoading(tier.id);

      const res = await fetch('https://mizar-new.vercel.app/v1/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tier: tier.id,
          email: user.email
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || data?.error || `Checkout failed (${res.status})`);
      }

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }

      if (data?.subscription_id) {
        window.alert('Trial started successfully. Refreshing billing state.');
        window.location.reload();
        return;
      }

      throw new Error('No checkout URL or subscription response returned.');
    } catch (err) {
      console.error('Checkout failed:', err);
      window.alert(err?.message || 'Something went wrong starting checkout.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleCTA = (tier) => {
    if (!user) {
      navigate(`/signup?plan=${tier.id}`);
      return;
    }

    startCheckout(tier);
  };

  return (
    <>
      <style>{`
        .pricing-section {
          position: relative;
          padding: 6rem 2rem;
          background: transparent;
          z-index: 1;
        }

        .pricing-container {
          max-width: 1180px;
          margin: 0 auto;
        }

        .pricing-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .pricing-title {
          margin: 0 0 1rem;
          font-family: 'Montserrat', sans-serif;
          font-weight: 800;
          font-size: 3rem;
          line-height: 1.08;
          letter-spacing: -0.02em;
          color: var(--text-primary);
        }

        .pricing-subtitle {
          margin: 0 auto 0.85rem;
          max-width: 760px;
          font-size: 1.08rem;
          line-height: 1.7;
          color: var(--text-secondary);
        }

        .pricing-trial-note {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.55rem 0.95rem;
          border-radius: 999px;
          border: 1px solid var(--border-subtle);
          background: rgba(255,255,255,0.04);
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.4;
        }

        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1.5rem;
          align-items: stretch;
          margin-bottom: 2rem;
        }

        .pricing-card {
          position: relative;
          display: flex;
          flex-direction: column;
          min-height: 100%;
          padding: 1.6rem;
          border: 1px solid var(--border-subtle);
          border-radius: 20px;
          background:
            linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%),
            rgba(7, 10, 12, 0.84);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.22);
          transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;
          overflow: hidden;
        }

        .pricing-card:hover {
          transform: translateY(-4px);
          border-color: rgba(217, 118, 66, 0.55);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.28);
        }

        .pricing-card.highlighted {
          border: 1px solid rgba(217, 118, 66, 0.9);
          box-shadow: 0 18px 42px rgba(217, 118, 66, 0.12);
        }

        .pricing-badge {
          position: absolute;
          top: 14px;
          right: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.35rem 0.72rem;
          border-radius: 999px;
          background: rgba(217, 118, 66, 0.16);
          border: 1px solid rgba(217, 118, 66, 0.5);
          color: #f6c3a7;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .pricing-card-header {
          padding-bottom: 1.2rem;
          margin-bottom: 1.2rem;
          border-bottom: 1px solid var(--border-subtle);
        }

        .pricing-eyebrow {
          display: inline-block;
          margin-bottom: 0.75rem;
          color: var(--accent-primary);
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .pricing-title-row {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          margin-bottom: 0.65rem;
        }

        .pricing-plan-icon {
          flex-shrink: 0;
          color: var(--accent-primary);
          opacity: 0.95;
        }

        .pricing-tier-name {
          margin: 0;
          font-family: 'Montserrat', sans-serif;
          font-size: 1.65rem;
          font-weight: 700;
          color: var(--text-primary);
          line-height: 1.1;
        }

        .pricing-tier-description {
          margin: 0 0 0.45rem;
          font-size: 1rem;
          line-height: 1.5;
          color: var(--text-secondary);
          min-height: 3rem;
        }

        .pricing-tier-subtext {
          margin: 0 0 1rem;
          font-size: 0.85rem;
          line-height: 1.45;
          color: var(--text-muted);
          min-height: 2.45rem;
        }

        .pricing-tier-price {
          display: flex;
          align-items: baseline;
          gap: 0.3rem;
          margin-bottom: 0.35rem;
          font-size: 2.8rem;
          font-weight: 800;
          line-height: 1;
          color: var(--text-primary);
        }

        .pricing-tier-price-unit {
          font-size: 1rem;
          font-weight: 500;
          color: var(--text-muted);
        }

        .pricing-tier-trial {
          margin: 0;
          font-size: 0.9rem;
          line-height: 1.4;
          color: var(--text-secondary);
        }

        .pricing-features {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
          margin-bottom: 1.4rem;
        }

        .pricing-feature {
          display: flex;
          align-items: flex-start;
          gap: 0.7rem;
          font-size: 0.95rem;
          line-height: 1.5;
          color: var(--text-secondary);
        }

        .pricing-feature-icon {
          flex-shrink: 0;
          margin-top: 0.08rem;
        }

        .pricing-cta {
          width: 100%;
          margin-top: auto;
          padding: 0.95rem 1rem;
          border-radius: 12px;
          border: 1px solid transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 0.98rem;
          font-weight: 700;
          cursor: pointer;
          transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease, color 0.18s ease;
        }

        .pricing-cta.primary {
          background: var(--accent-primary);
          color: #fff;
        }

        .pricing-cta.primary:hover {
          background: #c56838;
          transform: translateY(-1px);
        }

        .pricing-cta.secondary {
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          border-color: var(--border-primary);
        }

        .pricing-cta.secondary:hover {
          border-color: rgba(217, 118, 66, 0.65);
          background: rgba(255,255,255,0.06);
          transform: translateY(-1px);
        }

        .pricing-cta:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .pricing-enterprise-strip {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 1.25rem;
          padding: 1.35rem 1.4rem;
          margin-top: 1rem;
          border: 1px solid var(--border-subtle);
          border-radius: 18px;
          background:
            linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.015) 100%),
            rgba(7, 10, 12, 0.72);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .pricing-enterprise-copy {
          min-width: 0;
        }

        .pricing-enterprise-title {
          margin: 0 0 0.35rem;
          font-family: 'Montserrat', sans-serif;
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--text-primary);
          line-height: 1.35;
        }

        .pricing-enterprise-text {
          margin: 0;
          max-width: 760px;
          font-size: 0.94rem;
          line-height: 1.6;
          color: var(--text-secondary);
        }

        .pricing-enterprise-button {
          min-width: 190px;
          width: auto;
          flex-shrink: 0;
        }

        .pricing-proof {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.85rem;
          margin: 2rem 0 0;
        }

        .pricing-proof-pill {
          padding: 0.7rem 1rem;
          border-radius: 999px;
          border: 1px solid var(--border-subtle);
          background: rgba(255,255,255,0.03);
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.4;
        }

        :root[data-theme="light"] .pricing-trial-note {
          background: rgba(255,255,255,0.72);
          border-color: rgba(26, 22, 18, 0.08);
          color: var(--text-secondary);
        }

        :root[data-theme="light"] .pricing-card {
          background:
            linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(248,246,241,0.8) 100%),
            rgba(255,255,255,0.92);
          border-color: rgba(26, 22, 18, 0.08);
          box-shadow: 0 12px 32px rgba(26, 22, 18, 0.08);
        }

        :root[data-theme="light"] .pricing-card:hover {
          border-color: rgba(217, 118, 66, 0.45);
          box-shadow: 0 16px 36px rgba(26, 22, 18, 0.12);
        }

        :root[data-theme="light"] .pricing-card.highlighted {
          border-color: rgba(217, 118, 66, 0.72);
          box-shadow: 0 18px 38px rgba(217, 118, 66, 0.12);
        }

        :root[data-theme="light"] .pricing-card-header {
          border-bottom-color: rgba(26, 22, 18, 0.08);
        }

        :root[data-theme="light"] .pricing-badge {
          background: rgba(217, 118, 66, 0.12);
          border-color: rgba(217, 118, 66, 0.28);
          color: #9a4f2b;
        }

        :root[data-theme="light"] .pricing-cta.secondary {
          background: rgba(26, 22, 18, 0.03);
          border-color: rgba(26, 22, 18, 0.10);
          color: var(--text-primary);
        }

        :root[data-theme="light"] .pricing-cta.secondary:hover {
          background: rgba(26, 22, 18, 0.05);
          border-color: rgba(217, 118, 66, 0.38);
        }

        :root[data-theme="light"] .pricing-enterprise-strip {
          background:
            linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(248,246,241,0.78) 100%),
            rgba(255,255,255,0.92);
          border-color: rgba(26, 22, 18, 0.08);
          box-shadow: 0 10px 24px rgba(26, 22, 18, 0.06);
        }

        :root[data-theme="light"] .pricing-proof-pill {
          background: rgba(255,255,255,0.76);
          border-color: rgba(26, 22, 18, 0.08);
          color: var(--text-secondary);
        }

        @media (max-width: 1100px) {
          .pricing-grid {
            grid-template-columns: 1fr;
          }

          .pricing-tier-description,
          .pricing-tier-subtext {
            min-height: auto;
          }

          .pricing-enterprise-strip {
            grid-template-columns: 1fr;
            align-items: start;
          }

          .pricing-enterprise-button {
            width: 100%;
            min-width: 0;
          }
        }

        @media (max-width: 960px) {
          .pricing-section {
            padding: 5rem 1.25rem;
          }

          .pricing-title {
            font-size: 2.3rem;
          }
        }

        @media (max-width: 640px) {
          .pricing-section {
            padding: 4rem 1rem;
          }

          .pricing-title {
            font-size: 1.95rem;
          }

          .pricing-subtitle {
            font-size: 1rem;
          }

          .pricing-card {
            padding: 1.35rem 1.1rem 1.1rem;
          }

          .pricing-badge {
            position: static;
            margin-bottom: 0.85rem;
            width: fit-content;
          }

          .pricing-tier-price {
            font-size: 2.25rem;
          }

          .pricing-enterprise-strip {
            padding: 1.15rem;
          }
        }
      `}</style>

      <section className="pricing-section" id="pricing" aria-labelledby="pricing-title">
        <div className="pricing-container">
          <div className="pricing-header">
            <h2 className="pricing-title" id="pricing-title">
              Pricing for teams turning flight data into booking decisions
            </h2>
            <p className="pricing-subtitle">
              MIZAR predicts the probability a flight price will rise, so teams can act with confidence instead of guesswork.
            </p>
            <div className="pricing-trial-note">
              All plans start with a 14-day free trial.
            </div>
          </div>

          <div className="pricing-grid">
            {PRICING_TIERS.map((tier) => {
              const Icon = tier.icon;
              const isPrimary = tier.highlighted;
              const isLoading = checkoutLoading === tier.id;

              return (
                <article
                  key={tier.id}
                  className={`pricing-card ${tier.highlighted ? 'highlighted' : ''}`}
                  aria-label={`${tier.name} pricing tier`}
                >
                  {tier.badge && <div className="pricing-badge">{tier.badge}</div>}

                  <div className="pricing-card-header">
                    <div className="pricing-eyebrow">{tier.eyebrow}</div>

                    <div className="pricing-title-row">
                      <Icon className="pricing-plan-icon" size={20} strokeWidth={2} />
                      <h3 className="pricing-tier-name">{tier.name}</h3>
                    </div>

                    <p className="pricing-tier-description">{tier.description}</p>
                    <p className="pricing-tier-subtext">{tier.subtext}</p>

                    <div className="pricing-tier-price">
                      {tier.priceLabel}
                      {tier.priceUnit && (
                        <span className="pricing-tier-price-unit">{tier.priceUnit}</span>
                      )}
                    </div>

                    {tier.trial && (
                      <p className="pricing-tier-trial">{tier.trial}</p>
                    )}
                  </div>

                  <div className="pricing-features">
                    {tier.features.map((feature, index) => (
                      <div key={index} className="pricing-feature">
                        <div className="pricing-feature-icon">
                          <Check size={18} color="#5FD4A0" />
                        </div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => handleCTA(tier)}
                    disabled={isLoading}
                    className={`pricing-cta ${isPrimary ? 'primary' : 'secondary'}`}
                    aria-label={`${tier.cta} for ${tier.name}`}
                  >
                    {isLoading ? 'Starting...' : tier.cta}
                    <ArrowRight size={18} />
                  </button>
                </article>
              );
            })}
          </div>

          <div className="pricing-enterprise-strip">
            <div className="pricing-enterprise-copy">
              <h3 className="pricing-enterprise-title">
                Need custom deployment, security review, or tailored commercial terms?
              </h3>
              <p className="pricing-enterprise-text">
                We support larger platforms with white-label delivery, procurement support, custom SLAs, and deployment flexibility.
              </p>
            </div>

            <button
              onClick={() => navigate('/contact')}
              className="pricing-cta secondary pricing-enterprise-button"
              aria-label="Contact sales for enterprise pricing"
            >
              Contact Sales
              <ArrowRight size={18} />
            </button>
          </div>

          <div className="pricing-proof">
            <div className="pricing-proof-pill">Reduced overpayment on volatile routes</div>
            <div className="pricing-proof-pill">Increased confidence at booking</div>
            <div className="pricing-proof-pill">Clear decision signals instead of guesswork</div>
          </div>
        </div>
      </section>
    </>
  );
}
